/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { useTranslation } from 'next-i18next';
import IconWrapper from '@components/ui/Icons';
import Heading from '@components/ui/Heading';

import styles from './StoreInformation.module.scss';

export interface StoreInformationProps {
  store_name: string;
  address_line_1: string;
  address_line_2?: string;
  address_city: string;
  address_postalcode: string;
  address_province: string;
  phone: string;
  manager?: string;
  walk_directions?: string;
  public_transport_directions?: string;
  drive_directions?: string;
  bike_directions?: string;
}

const StoreInformation = ({
  store_name,
  address_line_1,
  address_line_2,
  address_city,
  address_postalcode,
  address_province,
  phone,
  manager,
  walk_directions,
  public_transport_directions,
  drive_directions,
  bike_directions,
}: StoreInformationProps) => {
  const { t } = useTranslation('stores');
  const formattedPhone = phone?.replace(/[^0-9.]/g, '');

  return (
    <>
      <Heading semanticLevel="h1" styleLevel="1">
        {store_name}
      </Heading>

      {/* TODO will My Store be set up on launch? */}

      <Heading semanticLevel="h2" styleLevel="4">
        {t('title.address')}
      </Heading>
      <p>
        {address_line_1}
        <br />
        {address_line_2 && (
          <>
            {`${address_line_2}`} <br />
          </>
        )}
        {`${address_city}, ${address_province}`}
        <br />
        {address_postalcode}
      </p>

      <Heading semanticLevel="h2" styleLevel="4">
        {t('title.phone')}
      </Heading>
      {formattedPhone && (
        <p>
          <a href={`tel:${formattedPhone}`}>{phone}</a>
        </p>
      )}

      {manager && (
        <>
          <Heading semanticLevel="h2" styleLevel="4">
            {t('title.manager')}
          </Heading>
          <p>{manager}</p>
        </>
      )}

      <Heading semanticLevel="h2" styleLevel="4">
        {t('title.directions')}
      </Heading>
      <ul className={styles.directions}>
        {walk_directions && (
          <li>
            <a
              href={walk_directions}
              target="_blank"
              rel="noreferrer"
              className={styles.directionsLink}
            >
              <IconWrapper height={24} width={28} icon="walk" />
              <span className={styles.screenReader}>
                {t('directions.walk')}
              </span>
            </a>
          </li>
        )}
        {bike_directions && (
          <li>
            <a
              href={bike_directions}
              target="_blank"
              rel="noreferrer"
              className={styles.directionsLink}
            >
              <IconWrapper height={24} width={24} icon="bike" />
              <span className={styles.screenReader}>
                {t('directions.bike')}
              </span>
            </a>
          </li>
        )}
        {public_transport_directions && (
          <li>
            <a
              href={public_transport_directions}
              target="_blank"
              rel="noreferrer"
              className={styles.directionsLink}
            >
              <IconWrapper height={24} width={24} icon="transit" />
              <span className={styles.screenReader}>
                {t('directions.transit')}
              </span>
            </a>
          </li>
        )}
        {drive_directions && (
          <li>
            <a
              href={drive_directions}
              target="_blank"
              rel="noreferrer"
              className={styles.directionsLink}
            >
              <IconWrapper height={24} width={24} icon="car" />
              <span className={styles.screenReader}>
                {t('directions.drive')}
              </span>
            </a>
          </li>
        )}
      </ul>
    </>
  );
};

export default StoreInformation;
